import axios from "axios";
import { MODE_DEMO, reponseDemo } from "./demoMode";
import { jetonActuel, oublierSession } from "./session";
// URL du backend. Ecrite en dur historiquement, ce qui empechait la
// preproduction de viser un autre service que la production. On peut
// desormais la surcharger au build via VITE_BACKEND_URL, la valeur de
// production restant la valeur par defaut.
export const APIURL =
  import.meta.env?.VITE_BACKEND_URL || "https://prod-f7nwx62ufq-ew.a.run.app/";
//export const APIURL = "https://back-lybo-admin.herokuapp.com/";
//export const APIURL = " http://localhost:8000/";

/*
  LE SERVEUR DE LA TABLETTE, POUR LA LECTURE DES CARTES.

  C'est le service Cloud Run `lybo-back-preprod` : malgre son nom c'est la
  PRODUCTION (voir la memoire « topologie prod reelle » — trois noms
  trompeurs). C'est lui qui porte `card-ocr`, la lecture des cartes, et c'est
  pour cela qu'on ne passe PAS par `APIURL` : les deux services n'exposent pas
  les memes routes, et un chemin de lecture envoye au mauvais service repond
  404 sans rien dire d'utile.

  La valeur par defaut est la production ; on peut la surcharger au build pour
  viser la preprod isolee, comme `VITE_BACKEND_URL`.
*/
export const LECTURE_URL =
  import.meta.env?.VITE_LECTURE_URL ||
  "https://lybo-back-preprod-f7nwx62ufq-ew.a.run.app/";

/**
 * Fait lire la carte de mutuelle d'un dossier par le serveur de la tablette.
 *
 * `POST <LECTURE_URL>/card-ocr/dossier/<id>/mutuelle`, corps `{}` (ou
 * `{ force: true }` pour relancer une lecture deja faite ou deja ratee).
 * Le serveur ecrit `mutuelleResume` sur le rendez-vous : l'ecoute Firestore
 * deja en place l'affichera d'elle-meme, on ne pose rien ici.
 *
 * Le jeton est demande au SDK au moment de l'appel, comme partout ailleurs
 * (voir Utils/session) — jamais lu dans localStorage.
 *
 * Ne leve pas : les erreurs reseau sont rendues comme un echec de lecture, ce
 * que l'ecran sait montrer. Une exception non rattrapee dans un effet React
 * demonte l'arbre.
 */
export async function lireMutuelleDossier(id, { force } = {}) {
  if (!id) return { success: false, raison: "identifiant manquant" };
  const jeton = await jetonActuel();
  try {
    const reponse = await axios({
      method: "POST",
      url: `${LECTURE_URL}card-ocr/dossier/${encodeURIComponent(id)}/mutuelle`,
      data: force ? { force: true } : {},
      headers: jeton ? { authorization: `Bearer ${jeton}` } : {},
      timeout: 60000,
      responseType: "json",
    });
    return reponse.data || { success: false };
  } catch (e) {
    // Pas de deconnexion sur un 401 ici : c'est un autre service que celui de
    // la session, et un refus de lecture ne doit pas sortir la secretaire.
    return {
      success: false,
      raison: e.response?.data?.raison || e.code || "reseau",
    };
  }
}

export const axiosInstance = axios.create({
  baseURL: APIURL,
  timeout: 8000,
  responseType: "json",
});

// Le jeton est pose sur chaque requete au moment ou elle part, par le SDK
// Firebase — jamais lu dans localStorage (audit, constat E6). Un en-tete
// `authorization` deja present est remplace : il serait perime.
axiosInstance.interceptors.request.use(async (config) => {
  const jeton = await jetonActuel();
  config.headers = config.headers || {};
  if (jeton) config.headers.authorization = `Bearer ${jeton}`;
  else delete config.headers.authorization;
  return config;
});

// Mode demonstration : on repond a la place du reseau, sans toucher aux
// ecrans. Une route non simulee part quand meme vers le vrai backend, donc
// rien n'est masque silencieusement. Inerte hors mode demonstration.
if (MODE_DEMO) {
  const vraiAdaptateur = axiosInstance.defaults.adapter;
  axiosInstance.defaults.adapter = async (config) => {
    const simulee = reponseDemo(
      String(config.method || "get").toLowerCase(),
      config.url
    );
    if (simulee === null) return vraiAdaptateur(config);
    // Petite latence : sans elle les ecrans n'affichent jamais leur etat de
    // chargement, et une capture d'ecran ne montrerait pas le comportement reel.
    await new Promise((r) => setTimeout(r, 220));
    return { data: simulee, status: 200, statusText: "OK", headers: {}, config };
  };
}

export default async function ExpressAPI(
  method = "GET",
  url = "/",
  data = [],
  responseType = "json"
) {
  try {
    const jeton = await jetonActuel();
    return await axios({
      method: method,
      url: APIURL + url,
      data: { data },
      headers: jeton ? { authorization: `Bearer ${jeton}` } : {},
      responseType: responseType,
    });
  } catch (e) {
    if (e.response && e.response.status === 401) {
      alert("Veuillez vous reconnecter");
      oublierSession();
      window.location.reload();
    }
  }
}
